import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { formatSourceLabel } from '@terra/ui-design-system';
import {
  effectiveScopeValue,
  SCOPE_LABEL_KEY,
} from './format-asset-list-display';

export function useAssetListDisplay() {
  const t = useTranslations('externalNetwork');
  const tDetail = useTranslations('externalNetwork.assetDetail');

  return useMemo(() => {
    const scopeLabel = (scope?: string | null): string | undefined => {
      if (!scope || scope === 'INHERIT') {
        return undefined;
      }

      if (scope === 'IN_SCOPE') {
        return t(SCOPE_LABEL_KEY.IN_SCOPE);
      }

      if (scope === 'OUT_OF_SCOPE') {
        return t(SCOPE_LABEL_KEY.OUT_OF_SCOPE);
      }

      if (scope === 'PENDING') {
        return t(SCOPE_LABEL_KEY.PENDING);
      }

      return formatSourceLabel({ sourceType: scope }) ?? undefined;
    };

    const scopeCells = ({
      scope,
      effectiveScope,
    }: {
      scope?: string | null;
      effectiveScope?: string | null;
    }) => {
      const resolved = effectiveScopeValue({ scope, effectiveScope });

      return {
        scope: resolved.scope,
        scopeLabel: scopeLabel(resolved.scope),
        scopeInherited: resolved.scopeInherited,
      };
    };

    return {
      scopeCells,
      formatSource: formatSourceLabel,
      tlsLabel: tDetail('tls'),
      topLevelLabel: tDetail('topLevel'),
      subdomainLevelLabel: tDetail('subdomain'),
      publicAccessLabel: (access?: string | null) => {
        if (access === 'PUBLIC') {
          return t('assetList.access.public');
        }

        if (access === 'RESTRICTED') {
          return t('assetList.access.restricted');
        }

        if (access === 'UNKNOWN') {
          return t('assetList.access.unknown');
        }

        return null;
      },
    };
  }, [t, tDetail]);
}
